import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { normalizePhone10 } from "../../services/halkMasasiApi";
import "./LookupPanel.css";

/**
 * LookupPanel
 * - Telefon input (TR) + normalize (10 hane)
 * - Enter/Submit davranışı
 * - Validasyon + hata gösterimi
 *
 * Props:
 *  - title?: string
 *  - subtitle?: string
 *  - initialValue?: string
 *  - loading?: boolean
 *  - disabled?: boolean               //   NEW: parent kilidi
 *  - error?: string | null
 *  - onSubmit: (phone10: string) => void | Promise<void>
 *  - onClearError?: () => void
 *  - showHeader?: boolean             //   NEW: “kutu içinde kutu” engeli
 *  - showNormalizeHint?: boolean      //   NEW: istersen gizle
 *  - dense?: boolean                  //   NEW: sıkı spacing (mobil)
 */
export default function LookupPanel({
  title = "Vatandaş Sorgula",
  subtitle = "Telefon numarası girin (10 hane).",
  initialValue = "",
  loading = false,
  disabled = false,
  error = null,
  onSubmit,
  onClearError,
  showHeader = true,
  showNormalizeHint = true,
  dense = false,
}) {
  const [raw, setRaw] = useState(String(initialValue || ""));
  const [localError, setLocalError] = useState("");
  const inputRef = useRef(null);

  const isDisabled = Boolean(disabled || loading);
  const hasError = Boolean(localError || error);

  const phone10 = useMemo(() => normalizePhone10(raw), [raw]);

  // input değişince hata temizle (daha temiz UX)
  useEffect(() => {
    if (localError) setLocalError("");
    if (error && onClearError) onClearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  const validate = useCallback(() => {
    const p = phone10;

    if (!p) return "Telefon numarası zorunlu.";
    if (p.length !== 10) return "Telefon numarası 10 hane olmalıdır.";
    if (!/^\d{10}$/.test(p)) return "Telefon numarası geçersiz.";

    // Türkiye GSM genelde 5xx ile başlar
    if (!p.startsWith("5")) return "Telefon numarası 5 ile başlamalıdır.";

    return "";
  }, [phone10]);

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();

      if (isDisabled) return;

      if (typeof onSubmit !== "function") {
        setLocalError("Sorgu işlemi tanımlı değil.");
        return;
      }

      const msg = validate();
      if (msg) {
        setLocalError(msg);
        return;
      }

      try {
        await onSubmit(phone10);
      } catch {
        if (!error) {
          setLocalError("Sorgu sırasında bir hata oluştu. Lütfen tekrar deneyin.");
        }
      }
    },
    [onSubmit, validate, phone10, error, isDisabled]
  );

  const handleClear = useCallback(() => {
    if (isDisabled) return;

    setRaw("");
    setLocalError("");
    if (error && onClearError) onClearError();
    window.setTimeout(() => inputRef.current?.focus?.(), 0);
  }, [error, onClearError, isDisabled]);

  const onChange = useCallback(
    (e) => {
      if (isDisabled) return;

      const v = e.target.value ?? "";
      const digits = String(v).replace(/\D+/g, "");
      setRaw(digits);
    },
    [isDisabled]
  );

  return (
    <section
      className={[
        "hm-lookup-panel",
        dense ? "hm-lookup-panel--dense" : "",
        isDisabled ? "hm-lookup-panel--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Vatandaş sorgulama paneli"
    >
      {showHeader ? (
        <div className="hm-lookup-head">
          <div className="hm-lookup-title">{title}</div>
          {subtitle ? <div className="hm-lookup-subtitle">{subtitle}</div> : null}
        </div>
      ) : null}

      <form className="hm-lookup-form" onSubmit={handleSubmit} role="search">
        <label className="hm-lookup-label" htmlFor="hm_phone">
          Telefon
        </label>

        <div className={`hm-lookup-inputWrap ${hasError ? "is-error" : ""}`}>
          <MagnifyingGlassIcon className="hm-lookup-ic" aria-hidden="true" />

          <input
            ref={inputRef}
            id="hm_phone"
            name="phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="5XXXXXXXXX"
            className="hm-lookup-input"
            value={raw}
            onChange={onChange}
            disabled={isDisabled}
            aria-invalid={hasError ? "true" : "false"}
            aria-describedby={hasError ? "hm_lookup_error" : undefined}
            maxLength={11}
          />

          {raw ? (
            <button
              type="button"
              className="hm-lookup-clear"
              onClick={handleClear}
              disabled={isDisabled}
              aria-label="Temizle"
              title="Temizle"
            >
              <XMarkIcon className="hm-lookup-clear-ic" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <div className="hm-lookup-meta">
          

          <button type="submit" className="hm-lookup-submit" disabled={isDisabled}>
            {loading ? "Sorgulanıyor..." : "Sorgula"}
          </button>
        </div>

        {hasError ? (
          <div id="hm_lookup_error" className="hm-lookup-error" role="alert" aria-live="polite">
            {localError || error}
          </div>
        ) : null}
      </form>
    </section>
  );
}